import type { CSSProperties } from 'react'
import type { BookingSource, BookingStatus } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { guestDisplayName } from '@/shared/lib/format'
import { pillStatusFlags } from '../model/pill'
import { statusToken } from '../model/status'
import s from './BookingPill.module.css'

type BookingPillProps = {
  id: string
  guestName: string
  /**
   * Причина, по которой период закрыт, — есть ТОЛЬКО у статуса `blocked`, и тогда гостя не
   * существует вовсе. Подставляется ВМЕСТО имени: «Без имени» здесь объявило бы человека,
   * которого никто не бронировал.
   *
   * ⚠️ Решает СТАТУС, а не наличие поля: причина в контракте необязательна, и ответ без неё схеме
   * не противоречит. У плитки полотна серверной подписи статуса под рукой нет (её несёт легенда, а
   * не ячейка), поэтому здесь единственное на весь фронт запасное слово — и оно про ПЕРИОД, а не
   * про гостя: соврать им нельзя ни при каком статусе.
   */
  blockReasonLabel?: string
  status: BookingStatus
  /**
   * Происхождение брони; `null` — не записано. Тогда пилюля не несёт ни цвета площадки, ни
   * атрибута `data-pf`, и в режиме «цвет по площадке» остаётся на статусном тоне: красить нечем,
   * а подставить нейтральный значило бы объявить площадку, которой нет.
   */
  source: BookingSource | null
  /** `null` — суммы у брони нет; пилюля её просто не рисует (даже у `checked_in`/`confirmed`). */
  amountLabel: string | null
  className?: string
  /** Вычисляемая геометрия (`gridColumn`/`gridRow`) — ставит полотно снаружи (задача 4), не пилюля. */
  style?: CSSProperties
  onClick?: () => void
  onPointerEnter?: () => void
  onPointerLeave?: () => void
}

/**
 * Пилюля брони на полотне шахматки (docs/reference/phase-3-mockup.md §6). Только ВИД одной
 * брони — раскладку по сетке, клик и hover-intent на реальных данных навешивает полотно снаружи
 * (задача 4/5); эта пилюля лишь пробрасывает готовые обработчики и вычисляемый `style` на корень.
 *
 * Раскраска по СТАТУСУ (режим по умолчанию): у каждого статуса СВОЙ процент заливки/рамки (макет
 * §6), поэтому цвет кодируют правила `[data-status]` в CSS-модуле, а не единая формула через
 * `statusToken` — та даёт только токен, не проценты. `statusToken` здесь пригождается ровно там,
 * где нужен чистый токен без процента: фолбэк `.root` (незнакомый статус) и точка «заехал».
 *
 * Раскраска по ПЛОЩАДКЕ: пилюля отдаёт САМ ЦВЕТ (`--pf` из `source.color`), а `data-pf` ставит
 * лишь как признак «цвет есть». ⚠️ Отдельного правила НА ПЛОЩАДКУ быть не должно — цвет приходит
 * с бронью, а справочник домена растёт сам. Правило в CSS-модуле ОДНО и читает эту переменную; включает
 * его атрибут `data-cb` на предке (`#chess-grid`), который ставит полотно, — здесь он не
 * проставляется.
 */
export function BookingPill({
  id,
  guestName,
  blockReasonLabel,
  status,
  source,
  amountLabel,
  className,
  style,
  onClick,
  onPointerEnter,
  onPointerLeave,
}: BookingPillProps) {
  const { isIn, isPending, dim, strikethrough, showsAmount } = pillStatusFlags(status)
  const showSum = showsAmount && amountLabel !== null

  return (
    <button
      type="button"
      className={cn(s.root, className)}
      // `--tint` — только фолбэк незнакомого статуса (см. `.root` в модуле); известные статусы
      // красятся точными процентами `[data-status]`, эту переменную не читая.
      style={{ ...style, '--tint': statusToken(status), ...(source ? { '--pf': source.color } : {}) } as CSSProperties}
      data-status={status}
      /* Код площадки — ТОЛЬКО как признак «цвет есть»: правило раскраски по площадке включается
         наличием атрибута, а сам цвет берётся из `--pf` рядом. Селекторов по конкретным кодам в
         CSS больше нет — их заменил один, читающий серверное значение. */
      data-pf={source?.code}
      data-booking-id={id}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {isIn && <span className={s.dot} aria-hidden="true" />}
      <span className={cn(s.name, dim && s.dim, strikethrough && s.strike)}>
        {status === 'blocked' ? (blockReasonLabel ?? 'Период закрыт') : guestDisplayName(guestName)}
      </span>
      {showSum && <span className={s.sum}>{amountLabel}</span>}
      {isPending && <span className={s.pending}>ожид</span>}
    </button>
  )
}
